import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCreateCategory, useUpdateCategory } from '../hooks'
import type { TechnologyCategory } from '@/types/entities'

const categorySchema = z.object({
    name: z.string().min(1, 'Name is required'),
})

type CategoryFormData = z.infer<typeof categorySchema>

interface CategoryDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    category: TechnologyCategory | null
}

export function CategoryDialog({ open, onOpenChange, category }: CategoryDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!category
    const createCategory = useCreateCategory()
    const updateCategory = useUpdateCategory()

    const form = useForm<CategoryFormData>({
        resolver: zodResolver(categorySchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    })

    useEffect(() => {
        if (open) {
            if (category) {
                form.reset({
                    name: category.name || '',
                })
            } else {
                form.reset({
                    name: '',
                })
            }
        }
    }, [open, category, form])

    const onSubmit = async (data: CategoryFormData) => {
        try {
            const submitData = {
                name: data.name,
            }
            
            if (isEditing && category) {
                await updateCategory.mutateAsync({
                    id: category.id,
                    data: submitData,
                })
            } else {
                await createCategory.mutateAsync(submitData)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Category' : 'Create Category'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update the category details.'
                            : 'Create a new technology category.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
                        <Input
                            id="name"
                            {...form.register('name')}
                            placeholder="Category name"
                            autoFocus
                        />
                        {form.formState.errors.name && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.name.message}
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={createCategory.isPending || updateCategory.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={createCategory.isPending || updateCategory.isPending}
                        >
                            {createCategory.isPending || updateCategory.isPending
                                ? t('common.saving')
                                : isEditing
                                ? t('common.save')
                                : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
